import BreadCrumb from "Components/Common/BreadCrumb";
import { useAppDispatch } from "Components/Hooks/hooks";
import CustomFormModal, { Field } from "Components/Modals/CustomFormModal";
import CategoriesTable from "Components/Settings/Categories/CategoriesTable";
import { useFormik } from "formik";
import { useMemo, useState } from "react";
import { Container } from "reactstrap";
import { manageCategory } from "slices/thunks";
import * as Yup from "yup";
import {
  VALIDATION_LIMITS,
  VALIDATION_MESSAGES,
  createNameValidation,
} from "Components/constants/validation";

export interface CategoryType {
  categoryName: string;
  id?: number;
}

const Categories = () => {
  const dispatch = useAppDispatch();
  const [isAddCategory, setIsCategory] = useState<boolean>(false);
  const [isEditCategory, setIsEditCategory] = useState<boolean>(false);
  const [isCategoryLoading, setIsCategoryLoading] = useState<boolean>(false);

  const validation = useFormik<CategoryType>({
    enableReinitialize: true,
    initialValues: {
      categoryName: "",
    },
    validationSchema: Yup.object({
      categoryName: Yup.string()
        .trim()
        .required(VALIDATION_MESSAGES.REQUIRED("Category Name"))
        .min(
          VALIDATION_LIMITS.NAME.MIN,
          VALIDATION_MESSAGES.MIN_LENGTH(
            "Category Name",
            VALIDATION_LIMITS.NAME.MIN
          )
        )
        .max(
          VALIDATION_LIMITS.NAME.MAX,
          VALIDATION_MESSAGES.MAX_LENGTH(
            "Category Name",
            VALIDATION_LIMITS.NAME.MAX
          )
        )
        .test(
          "valid-category-name",
          createNameValidation("Category Name").message,
          createNameValidation("Category Name").test
        ),
    }),
    onSubmit: async (values, { resetForm }) => {
      setIsCategoryLoading(true);
      if (values.id) {
        await dispatch(
          manageCategory({
            id: values.id,
            name: values.categoryName?.trim(),
          })
        )?.unwrap();
      } else {
        await dispatch(
          manageCategory({
            name: values.categoryName?.trim(),
          })
        )?.unwrap();
      }
      setIsCategory(false);
      setIsEditCategory(false);
      resetForm();
      setIsCategoryLoading(false);
    },
  });

  const CategoryFields: Field[] = useMemo(
    () => [
      {
        name: "categoryName",
        label: "Category Name",
        type: "text",
        placeholder: "Enter category name",
      },
    ],
    []
  );

  const handleCategoryModal = (isAdd: boolean) => {
    if (isAdd) {
      setIsCategory((prev) => !prev);
    } else {
      setIsEditCategory((prev) => !prev);
    }
  };

  const handleEditCategory = (cat: any) => {
    setIsEditCategory((prev) => !prev);
    validation.setFieldValue("categoryName", cat?.name);
    validation.setFieldValue("id", cat.id);
  };

  document.title = "Categories | EaziQuote";

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Categories" pageTitle="Settings" url="/settings" />

        <CategoriesTable
          onAddCategory={handleCategoryModal?.bind(null, true)}
          onEditCategory={handleEditCategory}
        />

        <CustomFormModal
          isOpen={isAddCategory || isEditCategory}
          onCancel={
            isAddCategory
              ? handleCategoryModal?.bind(null, true)
              : handleCategoryModal?.bind(null, false)
          }
          fields={CategoryFields}
          title={isAddCategory ? "Add Category" : "Edit Category"}
          submitText="Save Category"
          validation={validation}
          isLoading={isCategoryLoading}
        />
      </Container>
    </div>
  );
};

export default Categories;
